'use client';

import { c as _c } from "react/compiler-runtime";
import { TEST_IDS } from '@repo/sdk/constants';
import { Badge, Button, Checkbox, Collapsible, CollapsibleContent, CollapsibleTrigger, Input, PanelHeader, ScrollArea, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui/components';
import { closestCenter, DndContext, DragOverlay, KeyboardSensor, PointerSensor, pointerWithin, TouchSensor, useDroppable, useSensor, useSensors } from '@repo/ui/dnd';
import type { CollisionDetection, DragMoveEvent, PointerSensorOptions } from '@repo/ui/dnd';
import { CSS, SortableContext, useSortable, verticalListSortingStrategy } from '@repo/ui/dnd';
import { cn } from '@repo/ui/lib/utils';
import { ChevronDown, GripVertical, LocateFixed, LockKeyhole, Search, X } from '@repo/ui/icons';
import { type ReactNode, type PointerEvent as ReactPointerEvent, useRef, useState } from 'react';
import { moveTableColumnLayout, sameLayout, type TableColumnLayout, type TableColumnPlacement, type TableColumnRegion } from './context/table-layout.ts';
import { useNewColumnIds, useTableState } from './context/table-state-provider.tsx';
import { revealColumn } from './components/reveal-column.ts';
import { isSelectionColumn } from './components/selection-column';
import { getTableSettingsDragEndAnnouncement, getTableSettingsDropPlacement, getTableSettingsMoveAnnouncement, getTableSettingsNoMoveAnnouncement, getTableSettingsPosition, shouldActivateTableSettingsPointer, TABLE_COLUMN_REGION_LABELS, TABLE_SETTINGS_DND_ACTIVATION, TABLE_SETTINGS_KEYBOARD_COORDINATE_GETTER } from './settings-dnd.ts';
import { AddColumnMenu } from './settings-add-column.tsx';
import { ColumnFieldsPicker, useColumnFieldChoices } from './settings-column-fields.tsx';
import { DisplayChoicesPicker, formatDisplayChoices, useDisplayChoices } from './settings-display-choices.tsx';
import { formatItemsShown, ItemsShownPicker, useItemsShownChoices } from './settings-items-shown.tsx';
import type { TableColumnExamples } from './hooks/use-column-examples.ts';
import { useListedColumns } from './hooks/use-listed-columns.ts';
import type { Column, RowData, TableColumnPinningState } from './tanstack-table';
const regions = [{
  id: 'start',
  label: TABLE_COLUMN_REGION_LABELS.start
}, {
  id: 'end',
  label: TABLE_COLUMN_REGION_LABELS.end
}, {
  id: 'center',
  label: TABLE_COLUMN_REGION_LABELS.center
}] as const satisfies ReadonlyArray<{
  id: TableColumnRegion;
  label: string;
}>;
class TableSettingsPointerSensor extends PointerSensor {
  static activators = [{
    eventName: 'onPointerDown' as const,
    handler: ({
      nativeEvent
    }: ReactPointerEvent, {
      onActivation
    }: PointerSensorOptions) => {
      if (!shouldActivateTableSettingsPointer(nativeEvent)) return false;
      onActivation?.({
        event: nativeEvent
      });
      return true;
    }
  }];
}
const regionDropId = (region: TableColumnRegion) => `table-settings-region-${region}`;
const sameTableColumnLayout = (first: null | TableColumnLayout, second: null | TableColumnLayout) => first === second || first !== null && second !== null && sameLayout(first.columnOrder, second.columnOrder) && sameLayout(first.pinning.start, second.pinning.start) && sameLayout(first.pinning.end, second.pinning.end);
const getColumnLabel = <TData extends RowData,>(column: Column<TData, unknown>) => {
  const label = column.columnDef.meta?.label;
  if (label) return label;
  if (typeof column.columnDef.header === 'string') return column.columnDef.header;
  return column.id.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim().replace(/^./, character => character.toUpperCase());
};
const getRegion = <TData extends RowData,>(column: Column<TData, unknown>, pinning: TableColumnPinningState): TableColumnRegion => {
  if (pinning.start?.includes(column.id)) return 'start';
  if (pinning.end?.includes(column.id)) return 'end';
  return 'center';
};
const getDropRegion = (id: number | string): TableColumnRegion | undefined => regions.find(region => regionDropId(region.id) === id)?.id;
const tableSettingsCollisionDetection: CollisionDetection = args => {
  if (!args.pointerCoordinates) return closestCenter(args);
  const activeContainer = args.droppableContainers.find(container => container.id === args.active.id);
  const activeRect = activeContainer?.data.current?.getTableSettingsRect?.();
  if (activeRect && activeRect.top <= args.pointerCoordinates.y && activeRect.bottom >= args.pointerCoordinates.y && activeRect.left <= args.pointerCoordinates.x && activeRect.right >= args.pointerCoordinates.x) {
    return [{
      id: args.active.id
    }];
  }
  const pointerCollisions = pointerWithin(args);
  const columnCollisions = pointerCollisions.filter(collision => collision.id !== args.active.id && !getDropRegion(collision.id));
  if (columnCollisions.length > 0) return columnCollisions;
  const targetRegion = pointerCollisions.map(collision => getDropRegion(collision.id)).find(region => region !== undefined);
  if (!targetRegion) return pointerCollisions;
  const closestColumns = closestCenter({
    ...args,
    droppableContainers: args.droppableContainers.filter(container => container.id !== args.active.id && container.data.current?.tableSettingsRegion === targetRegion)
  });
  return closestColumns.length > 0 ? closestColumns : pointerCollisions;
};
export const TableSettingsPanel = t0 => {
  const $ = _c(60);
  const {
    examples,
    onClose
  } = t0;
  const {
    columnOrder,
    columnPinning,
    columnVisibility,
    resetSettings,
    retrySave,
    saveStatus,
    setColumnOrder,
    setColumnPinning,
    table,
    tableRef
  } = useTableState(_temp);
  const [activeColumnId, setActiveColumnId] = useState(null);
  const [activeDropRegion, setActiveDropRegion] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      message: "",
      sequence: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [announcement, setAnnouncement] = useState(t1);
  const previewLayoutRef = useRef(null);
  const [previewLayout, setPreviewLayout] = useState(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);
  const allColumns = table.getAllLeafColumns().filter(_temp2);
  const columnsById = new Map(allColumns.map(_temp3));
  const {
    isListed,
    isSearchActive,
    listsShownOnly,
    matchingColumnIds,
    search,
    searchResultMessage,
    setSearch
  } = useListedColumns({
    columns: allColumns,
    columnVisibility,
    getLabel: getColumnLabel
  });
  const activeColumn = activeColumnId ? columnsById.get(activeColumnId) : undefined;
  const hideableColumns = allColumns.filter(_temp4);
  const hiddenColumns = hideableColumns.filter(_temp5);
  const canClearAll = hiddenColumns.length < hideableColumns.length;
  const setAllVisible = visible => {
    table.setColumnVisibility(visibility => ({
      ...visibility,
      ...Object.fromEntries(hideableColumns.map(column_3 => [column_3.id, visible]))
    }));
    searchRef.current?.focus();
  };
  const showAll = () => {
    const shownIds = new Set(hiddenColumns.map(_temp6));
    const order = table.getAllLeafColumns().map(_temp7);
    setColumnOrder([...order.filter(id => !shownIds.has(id)), ...order.filter(id_0 => shownIds.has(id_0))]);
    setAllVisible(true);
  };
  let t2;
  if ($[1] !== isSearchActive || $[2] !== listsShownOnly) {
    t2 = () => {
      if (!listsShownOnly || isSearchActive) {
        return;
      }
      const checkboxes = [...(listRef.current?.querySelectorAll("[role=\"checkbox\"]:not([data-disabled]):not([data-column-field]):not([data-display-choice])") ?? [])];
      const index = checkboxes.findIndex(_temp8);
      if (index === -1) {
        return;
      }
      (checkboxes[index + 1] ?? checkboxes[index - 1] ?? searchRef.current)?.focus();
    };
    $[1] = isSearchActive;
    $[2] = listsShownOnly;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const keepFocusInList = t2;
  let t3;
  if ($[4] !== columnOrder) {
    t3 = id_2 => !columnOrder.includes(id_2);
    $[4] = columnOrder;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const baseColumnOrder = [...columnOrder.filter(id_1 => columnsById.has(id_1)), ...allColumns.map(_temp9).filter(t3)];
  const displayedLayout = previewLayout ?? {
    columnOrder: baseColumnOrder,
    pinning: columnPinning
  };
  const getColumns = ids => ids.flatMap(id_3 => {
    const column_7 = columnsById.get(id_3);
    return column_7 ? [column_7] : [];
  });
  const pinnedColumnIds = new Set([...(displayedLayout.pinning.start ?? []), ...(displayedLayout.pinning.end ?? [])]);
  const columns = {
    center: getColumns(displayedLayout.columnOrder.filter(id_4 => !pinnedColumnIds.has(id_4))),
    end: getColumns(displayedLayout.pinning.end ?? []),
    start: getColumns(displayedLayout.pinning.start ?? [])
  };
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      activationConstraint: TABLE_SETTINGS_DND_ACTIVATION.pointer
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      activationConstraint: TABLE_SETTINGS_DND_ACTIVATION.touch
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      coordinateGetter: TABLE_SETTINGS_KEYBOARD_COORDINATE_GETTER
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const sensors = useSensors(useSensor(TableSettingsPointerSensor, t4), useSensor(TouchSensor, t5), useSensor(KeyboardSensor, t6));
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = message => setAnnouncement(current => ({
      message,
      sequence: current.sequence + 1
    }));
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const announce = t7;
  const locateColumn = id_5 => {
    if (tableRef.current) {
      revealColumn(tableRef.current, id_5);
    }
  };
  const getTargetRegion = overId => {
    const overColumn = columnsById.get(overId);
    return getDropRegion(overId) ?? (overColumn ? getRegion(overColumn, columnPinning) : undefined);
  };
  const moveColumn = t8 => {
    const {
      activeId,
      nextLayout,
      overId: overId_0,
      placement,
      targetRegion
    } = t8;
    const activeColumn_0 = columnsById.get(activeId);
    if (!activeColumn_0) {
      return;
    }
    const label = getColumnLabel(activeColumn_0);
    if (activeColumn_0.columnDef.meta?.lockedPinning) {
      announce(`${label} is locked and cannot be moved.`);
      return;
    }
    const sourceRegion = getRegion(activeColumn_0, columnPinning);
    if (isSearchActive && sourceRegion === targetRegion) {
      announce(`Clear search before reordering ${label} within ${getRegionLabel(targetRegion)}.`);
      return;
    }
    const next = nextLayout ?? moveTableColumnLayout({
      columnOrder,
      leafColumnIds: allColumns.map(_temp10),
      lockedPinning: {
        end: allColumns.filter(_temp11).map(_temp12),
        start: allColumns.filter(_temp13).map(_temp14)
      },
      move: {
        activeId,
        isSearchActive,
        overId: overId_0,
        placement,
        targetRegion
      },
      pinning: columnPinning
    });
    const columnOrderChanged = !sameLayout(columnOrder, next.columnOrder);
    const pinningChanged = !sameLayout(columnPinning.start, next.pinning.start) || !sameLayout(columnPinning.end, next.pinning.end);
    if (!columnOrderChanged && !pinningChanged) {
      announce(getTableSettingsNoMoveAnnouncement(label));
      return;
    }
    if (columnOrderChanged) {
      setColumnOrder(next.columnOrder);
    }
    if (pinningChanged) {
      setColumnPinning(next.pinning);
    }
    const destination = getRegionLabel(targetRegion);
    const {
      position,
      total
    } = getTableSettingsPosition({
      id: activeId,
      layout: next,
      leafColumnIds: allColumns.filter(isListed).map(_temp15),
      region: targetRegion
    });
    announce(getTableSettingsMoveAnnouncement({
      destination,
      label,
      position,
      total
    }));
    queueMicrotask(() => document.getElementById(`table-settings-grip-${activeId}`)?.focus());
    requestAnimationFrame(() => locateColumn(activeId));
  };
  const getPreviewLayout = t9 => {
    const {
      activeId: activeId_0,
      overId: overId_1,
      placement: placement_0,
      targetRegion: targetRegion_0
    } = t9;
    const activeColumn_1 = columnsById.get(activeId_0);
    if (!activeColumn_1 || activeColumn_1.columnDef.meta?.lockedPinning) {
      return null;
    }
    const sourceRegion_0 = getRegion(activeColumn_1, columnPinning);
    if (sourceRegion_0 === targetRegion_0) {
      return null;
    }
    return moveTableColumnLayout({
      columnOrder,
      leafColumnIds: allColumns.map(_temp16),
      lockedPinning: {
        end: allColumns.filter(_temp17).map(_temp18),
        start: allColumns.filter(_temp19).map(_temp20)
      },
      move: {
        activeId: activeId_0,
        isSearchActive,
        overId: overId_1,
        placement: placement_0,
        targetRegion: targetRegion_0
      },
      pinning: columnPinning
    });
  };
  const updateDragPreview = t10 => {
    const {
      active,
      delta,
      over
    } = t10;
    const activeId_1 = String(active.id);
    const currentPreview = previewLayoutRef.current;
    if (over && String(over.id) === activeId_1 && currentPreview) {
      setActiveDropRegion(currentPreview.region);
      return;
    }
    const targetRegion_1 = over ? getTargetRegion(String(over.id)) : undefined;
    setActiveDropRegion(targetRegion_1 ?? null);
    const overRect = over ? over.data.current?.getTableSettingsRect?.() ?? over.rect : undefined;
    const nextPreview = over && overRect && targetRegion_1 ? getPreviewLayout({
      activeId: activeId_1,
      overId: getDropRegion(over.id) ? undefined : String(over.id),
      placement: getTableSettingsDropPlacement({
        activeRect: active.rect.current.translated,
        deltaY: delta.y,
        overRect
      }),
      targetRegion: targetRegion_1
    }) : null;
    previewLayoutRef.current = nextPreview && targetRegion_1 ? {
      layout: nextPreview,
      region: targetRegion_1
    } : null;
    setPreviewLayout(current_0 => sameTableColumnLayout(current_0, nextPreview) ? current_0 : nextPreview);
  };
  const getLabelForId = id_6 => {
    const column_19 = columnsById.get(String(id_6));
    if (column_19) {
      return getColumnLabel(column_19);
    }
    const region = getDropRegion(id_6);
    return region ? getRegionLabel(region) : "column settings";
  };
  let t11;
  if ($[10] !== onClose) {
    t11 = onClose && <Button aria-label="Close column settings" onClick={onClose} size="icon-sm" variant="ghost"><X /></Button>;
    $[10] = onClose;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== t11) {
    t12 = <PanelHeader description="Show, hide, pin and reorder columns" title="Column settings" data-sentry-element="PanelHeader" data-sentry-source-file="settings.tsx">{t11}</PanelHeader>;
    $[12] = t11;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Search aria-hidden="true" className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" data-sentry-element="Search" data-sentry-source-file="settings.tsx" />;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] !== setSearch) {
    t14 = event => setSearch(event.target.value);
    $[15] = setSearch;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== search || $[18] !== t14) {
    t15 = <Input aria-label="Search columns" className="pr-8 pl-8" onChange={t14} placeholder="Search columns" ref={searchRef} value={search} data-sentry-element="Input" data-sentry-source-file="settings.tsx" />;
    $[17] = search;
    $[18] = t14;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== search || $[21] !== setSearch) {
    t16 = search && <Button aria-label="Clear search" className="absolute top-1/2 right-1 -translate-y-1/2" onClick={() => setSearch("")} size="icon-xs" variant="ghost"><X /></Button>;
    $[20] = search;
    $[21] = setSearch;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== t15 || $[24] !== t16) {
    t17 = <div className="relative flex-1">{t13}{t15}{t16}</div>;
    $[23] = t15;
    $[24] = t16;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== examples || $[27] !== listsShownOnly) {
    t18 = listsShownOnly && <AddColumnMenu announce={announce} examples={examples} getLabel={getColumnLabel} />;
    $[26] = examples;
    $[27] = listsShownOnly;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== t17 || $[30] !== t18) {
    t19 = <div className="flex items-center gap-2">{t17}{t18}</div>;
    $[29] = t17;
    $[30] = t18;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== searchResultMessage) {
    t20 = <p className="text-xs text-muted-foreground" role="status">{searchResultMessage}</p>;
    $[32] = searchResultMessage;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  const t21 = isSearchActive ? null : <div className="flex items-center gap-1"><Button disabled={hiddenColumns.length === 0} onClick={showAll} size="xs" variant="ghost">Show all</Button><Button disabled={!canClearAll} onClick={() => setAllVisible(false)} size="xs" variant="ghost">Clear all</Button></div>;
  let t22;
  if ($[34] !== t20 || $[35] !== t21) {
    t22 = <div className="mt-2 flex items-center justify-between gap-2">{t20}{t21}</div>;
    $[34] = t20;
    $[35] = t21;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== t19 || $[38] !== t22) {
    t23 = <div className="shrink-0 border-b px-4 py-3">{t19}{t22}</div>;
    $[37] = t19;
    $[38] = t22;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  const t28 = "space-y-4 [overflow-anchor:none]";
  const t29 = regions.map(region_0 => {
    const regionColumns = columns[region_0.id].filter(column_20 => matchingColumnIds.has(column_20.id) && isListed(column_20));
    return <ColumnRegion columns={regionColumns} isActive={activeDropRegion === region_0.id} key={region_0.id} label={region_0.label} onHide={keepFocusInList} onLocate={locateColumn} region={region_0.id} visibility={columnVisibility} />;
  });
  let t30;
  if ($[40] !== t29) {
    t30 = <div className={t28} ref={listRef}>{t29}</div>;
    $[40] = t29;
    $[41] = t30;
  } else {
    t30 = $[41];
  }
  let t31;
  if ($[42] !== activeColumn) {
    t31 = activeColumn && <ColumnDragOverlay column={activeColumn} />;
    $[42] = activeColumn;
    $[43] = t31;
  } else {
    t31 = $[43];
  }
  let t32;
  if ($[44] !== t31) {
    t32 = <DragOverlay dropAnimation={null} data-sentry-element="DragOverlay" data-sentry-source-file="settings.tsx">{t31}</DragOverlay>;
    $[44] = t31;
    $[45] = t32;
  } else {
    t32 = $[45];
  }
  let t33;
  if ($[46] !== isSearchActive || $[47] !== matchingColumnIds) {
    t33 = isSearchActive && matchingColumnIds.size === 0 && <p className="py-8 text-center text-sm text-muted-foreground">No columns match your search.</p>;
    $[46] = isSearchActive;
    $[47] = matchingColumnIds;
    $[48] = t33;
  } else {
    t33 = $[48];
  }
  let t34;
  if ($[49] !== retrySave || $[50] !== saveStatus) {
    t34 = saveStatus === "error" && <SaveError retrySave={retrySave} />;
    $[49] = retrySave;
    $[50] = saveStatus;
    $[51] = t34;
  } else {
    t34 = $[51];
  }
  let t35;
  if ($[52] !== resetSettings) {
    t35 = <Button className="w-full" onClick={resetSettings} variant="outline" data-sentry-element="Button" data-sentry-source-file="settings.tsx">Reset</Button>;
    $[52] = resetSettings;
    $[53] = t35;
  } else {
    t35 = $[53];
  }
  let t36;
  if ($[54] !== t34 || $[55] !== t35) {
    t36 = <footer className="flex shrink-0 flex-col gap-3 border-t px-4 py-3">{t34}{t35}</footer>;
    $[54] = t34;
    $[55] = t35;
    $[56] = t36;
  } else {
    t36 = $[56];
  }
  let t37;
  if ($[57] !== announcement.message || $[58] !== announcement.sequence) {
    t37 = <p aria-live="polite" className="sr-only" role="status"><span key={announcement.sequence}>{announcement.message}</span></p>;
    $[57] = announcement.message;
    $[58] = announcement.sequence;
    $[59] = t37;
  } else {
    t37 = $[59];
  }
  return <section aria-label="Column settings" className="flex h-full min-h-0 w-full min-w-0 flex-col bg-background" data-testid={TEST_IDS.table.columnSettingsPanel} data-sentry-component="TableSettingsPanel" data-sentry-source-file="settings.tsx">{t12}{t23}<ScrollArea className="min-h-0 flex-1 px-4 py-3" data-sentry-element="ScrollArea" data-sentry-source-file="settings.tsx"><DndContext accessibility={{
        announcements: {
          onDragCancel: t24 => {
            const {
              active: active_0
            } = t24;
            return `${getLabelForId(active_0.id)} move cancelled.`;
          },
          onDragEnd: getTableSettingsDragEndAnnouncement,
          onDragOver: t25 => {
            const {
              active: active_1,
              over: over_0
            } = t25;
            return over_0 && over_0.id !== active_1.id ? `${getLabelForId(active_1.id)} over ${getLabelForId(over_0.id)}.` : undefined;
          },
          onDragStart: t26 => {
            const {
              active: active_2
            } = t26;
            return `Moving ${getLabelForId(active_2.id)}.`;
          }
        },
        screenReaderInstructions: {
          draggable: "Use space to pick up a column, arrow keys to move it, and space to drop it."
        }
      }} collisionDetection={tableSettingsCollisionDetection} onDragCancel={() => {
        previewLayoutRef.current = null;
        setActiveColumnId(null);
        setActiveDropRegion(null);
        setPreviewLayout(null);
      }} onDragEnd={event_0 => {
        const {
          active: active_3,
          over: over_1
        } = event_0;
        const activeId_2 = String(active_3.id);
        const pendingPreview = previewLayoutRef.current;
        previewLayoutRef.current = null;
        setActiveColumnId(null);
        setActiveDropRegion(null);
        setPreviewLayout(null);
        if (pendingPreview && over_1 && (String(over_1.id) === activeId_2 || getTargetRegion(String(over_1.id)) === pendingPreview.region)) {
          moveColumn({
            activeId: activeId_2,
            nextLayout: pendingPreview.layout,
            targetRegion: pendingPreview.region
          });
          return;
        }
        if (!over_1) {
          announce(getTableSettingsNoMoveAnnouncement(getLabelForId(active_3.id)));
          return;
        }
        const overId_2 = String(over_1.id);
        const targetRegion_2 = getTargetRegion(overId_2);
        if (targetRegion_2) {
          const overRect_0 = over_1.data.current?.getTableSettingsRect?.() ?? over_1.rect;
          moveColumn({
            activeId: activeId_2,
            overId: getDropRegion(overId_2) ? undefined : overId_2,
            placement: getTableSettingsDropPlacement({
              activeRect: active_3.rect.current.translated,
              deltaY: event_0.delta.y,
              overRect: overRect_0
            }),
            targetRegion: targetRegion_2
          });
        }
      }} onDragMove={updateDragPreview} onDragOver={updateDragPreview} onDragStart={t27 => {
        const {
          active: active_4
        } = t27;
        previewLayoutRef.current = null;
        setActiveColumnId(String(active_4.id));
        setActiveDropRegion(null);
        setPreviewLayout(null);
        announce(`Moving ${getLabelForId(active_4.id)}.`);
      }} sensors={sensors} data-sentry-element="DndContext" data-sentry-source-file="settings.tsx">{t30}{t32}</DndContext>{t33}</ScrollArea>{t36}{t37}</section>;
};
const ColumnRegion = t0 => {
  const $ = _c(48);
  const {
    columns,
    isActive,
    label,
    onHide,
    onLocate,
    region,
    visibility
  } = t0;
  let t1;
  if ($[0] !== region) {
    t1 = regionDropId(region);
    $[0] = region;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      id: t1
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    setNodeRef
  } = useDroppable(t2);
  let T0;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[4] !== columns || $[5] !== isActive || $[6] !== label || $[7] !== region || $[8] !== setNodeRef) {
    const draggableColumns = columns.filter(_temp21);
    t6 = `table-settings-${region}-heading`;
    t7 = "ColumnRegion";
    t8 = "settings.tsx";
    const t10 = `table-settings-${region}-heading`;
    if ($[17] !== label || $[18] !== t10) {
      t9 = <h3 className="mb-2 text-xs font-medium tracking-wide text-muted-foreground uppercase" id={t10}>{label}</h3>;
      $[17] = label;
      $[18] = t10;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    t4 = `min-h-10 rounded-md border border-dashed p-1 transition-colors ${isActive ? "border-primary bg-primary/5" : "border-border"}`;
    t5 = setNodeRef;
    T0 = SortableContext;
    t3 = draggableColumns.map(_temp22);
    $[4] = columns;
    $[5] = isActive;
    $[6] = label;
    $[7] = region;
    $[8] = setNodeRef;
    $[9] = T0;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    T0 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
    t9 = $[16];
  }
  let t10;
  if ($[20] !== columns || $[21] !== onHide || $[22] !== onLocate || $[23] !== region || $[24] !== visibility) {
    let t11;
    if ($[26] !== onHide || $[27] !== onLocate || $[28] !== region || $[29] !== visibility) {
      t11 = column_1 => <ColumnRow column={column_1} isVisible={visibility[column_1.id] ?? column_1.getIsVisible()} key={column_1.id} onHide={onHide} onLocate={() => onLocate(column_1.id)} region={region} />;
      $[26] = onHide;
      $[27] = onLocate;
      $[28] = region;
      $[29] = visibility;
      $[30] = t11;
    } else {
      t11 = $[30];
    }
    t10 = columns.map(t11);
    $[20] = columns;
    $[21] = onHide;
    $[22] = onLocate;
    $[23] = region;
    $[24] = visibility;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[31] !== T0 || $[32] !== t10 || $[33] !== t3) {
    t11 = <T0 items={t3} strategy={verticalListSortingStrategy} data-sentry-element="SortableContext" data-sentry-source-file="settings.tsx">{t10}</T0>;
    $[31] = T0;
    $[32] = t10;
    $[33] = t3;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== columns.length) {
    t12 = columns.length === 0 && <p className="px-2 py-1.5 text-xs text-muted-foreground">Drop columns here</p>;
    $[35] = columns.length;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== t11 || $[38] !== t12 || $[39] !== t4 || $[40] !== t5) {
    t13 = <div className={t4} ref={t5}>{t11}{t12}</div>;
    $[37] = t11;
    $[38] = t12;
    $[39] = t4;
    $[40] = t5;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== t13 || $[43] !== t6 || $[44] !== t7 || $[45] !== t8 || $[46] !== t9) {
    t14 = <section aria-labelledby={t6} data-sentry-component={t7} data-sentry-source-file={t8}>{t9}{t13}</section>;
    $[42] = t13;
    $[43] = t6;
    $[44] = t7;
    $[45] = t8;
    $[46] = t9;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  return t14;
};
const ColumnDragOverlay = t0 => {
  const $ = _c(10);
  const {
    column
  } = t0;
  let t1;
  if ($[0] !== column) {
    t1 = getColumnLabel(column);
    $[0] = column;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `Dragging ${t1}`;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="grid size-6 place-items-center text-muted-foreground"><GripVertical className="size-3" data-sentry-element="GripVertical" data-sentry-source-file="settings.tsx" /></span>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== column) {
    t4 = getColumnLabel(column);
    $[3] = column;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4) {
    t5 = <span className="min-w-0 flex-1 truncate text-sm">{t4}</span>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t2 || $[8] !== t5) {
    t6 = <div aria-label={t2} className="flex w-full items-center gap-2 rounded-sm border bg-background px-1.5 py-1.5 shadow-lg" role="group" data-sentry-component="ColumnDragOverlay" data-sentry-source-file="settings.tsx">{t3}{t5}</div>;
    $[7] = t2;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};

/**
 * More choices for a shown column, behind a button at the end of its row that expands them under
 * it, such as its Column fields.
 */
type ColumnRowExtras = {
  content: ReactNode;
  /** Marks the button with a dot while collapsed, for something New inside. */
  hasNew: boolean;
  /** The button's text, summing up the current choice, such as "2 of 3 fields". */
  label: string;
};

/**
 * A column row's extras: the Items shown and Column fields its column offers, as a List cell whose
 * fields are the parts of each item offers both, Items shown first, then its Display choices. The button sums up the
 * first two and any choice off its default, else every choice.
 */
const useColumnRowExtras = columnId => {
  const $ = _c(33);
  const fieldChoices = useColumnFieldChoices(columnId);
  const itemsShownChoices = useItemsShownChoices(columnId);
  const displayChoices = useDisplayChoices(columnId);
  if (!fieldChoices && !itemsShownChoices && !displayChoices) {
    return;
  }
  let fieldTiedChoices;
  let t0;
  if ($[0] !== displayChoices?.choices || $[1] !== fieldChoices?.fields) {
    const choices = displayChoices?.choices ?? [];
    let t1;
    if ($[4] !== fieldChoices?.fields) {
      t1 = t2 => {
        const {
          choice
        } = t2;
        return fieldChoices?.fields.some(field => field.id === choice.field);
      };
      $[4] = fieldChoices?.fields;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    fieldTiedChoices = choices.filter(t1);
    t0 = choices.filter(choice_0 => !fieldTiedChoices.includes(choice_0));
    $[0] = displayChoices?.choices;
    $[1] = fieldChoices?.fields;
    $[2] = fieldTiedChoices;
    $[3] = t0;
  } else {
    fieldTiedChoices = $[2];
    t0 = $[3];
  }
  const otherChoices = t0;
  const t1 = fieldChoices && `${fieldChoices.shownIds.length} of ${fieldChoices.fields.length} fields`;
  let t2;
  if ($[6] !== itemsShownChoices) {
    t2 = itemsShownChoices && formatItemsShown(itemsShownChoices.count);
    $[6] = itemsShownChoices;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t1 || $[9] !== t2) {
    t3 = [t1, t2].filter(Boolean);
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const labels = t3;
  let t4;
  if ($[11] !== displayChoices) {
    t4 = displayChoices && formatDisplayChoices(displayChoices.choices.filter(_temp23));
    $[11] = displayChoices;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const offDefault = t4;
  let t5;
  if ($[13] !== itemsShownChoices) {
    t5 = itemsShownChoices && <ItemsShownPicker {...itemsShownChoices} />;
    $[13] = itemsShownChoices;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== fieldChoices || $[16] !== fieldTiedChoices) {
    t6 = fieldChoices && <ColumnFieldsPicker {...fieldChoices} fieldChoices={fieldTiedChoices} />;
    $[15] = fieldChoices;
    $[16] = fieldTiedChoices;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== columnId || $[19] !== otherChoices) {
    t7 = otherChoices.length > 0 && <DisplayChoicesPicker choices={otherChoices} columnId={columnId} />;
    $[18] = columnId;
    $[19] = otherChoices;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t5 || $[22] !== t6 || $[23] !== t7) {
    t8 = <>{t5}{t6}{t7}</>;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const t9 = (fieldChoices?.newIds.size ?? 0) > 0;
  let t10;
  if ($[25] !== displayChoices || $[26] !== labels || $[27] !== offDefault) {
    t10 = labels.length === 0 && displayChoices ? formatDisplayChoices(displayChoices.choices) : [...labels, offDefault].filter(Boolean).join(" \xB7 ");
    $[25] = displayChoices;
    $[26] = labels;
    $[27] = offDefault;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t10 || $[30] !== t8 || $[31] !== t9) {
    t11 = {
      content: t8,
      hasNew: t9,
      label: t10
    };
    $[29] = t10;
    $[30] = t8;
    $[31] = t9;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  return t11;
};
const ColumnRow = t0 => {
  const $ = _c(73);
  const {
    column,
    isVisible,
    onHide,
    onLocate,
    region
  } = t0;
  const isLocked = Boolean(column.columnDef.meta?.lockedPinning);
  const rowRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => rowRef.current?.getBoundingClientRect();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== region) {
    t2 = {
      getTableSettingsRect: t1,
      tableSettingsRegion: region
    };
    $[1] = region;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== column.id || $[4] !== isLocked || $[5] !== t2) {
    t3 = {
      data: t2,
      disabled: isLocked,
      id: column.id
    };
    $[3] = column.id;
    $[4] = isLocked;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    attributes,
    isDragging,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition
  } = useSortable(t3);
  let t4;
  if ($[7] !== column) {
    t4 = column.getCanHide();
    $[7] = column;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const canHide = t4;
  const t5 = useNewColumnIds();
  let t6;
  if ($[9] !== column.id || $[10] !== t5) {
    t6 = t5.has(column.id);
    $[9] = column.id;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const isNew = t6;
  const rowExtras = useColumnRowExtras(column.id);
  const extras = isVisible ? rowExtras : undefined;
  const [isExpanded, setIsExpanded] = useState(false);
  if (isExpanded && !extras) {
    setIsExpanded(false);
  }
  const showsNewDot = extras?.hasNew && !isExpanded;
  const checkboxId = `table-settings-visibility-${column.id}`;
  const t7 = isDragging ? 0 : undefined;
  let t8;
  if ($[12] !== isDragging || $[13] !== transform) {
    t8 = isDragging ? undefined : CSS.Transform.toString(transform);
    $[12] = isDragging;
    $[13] = transform;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t7 || $[16] !== t8 || $[17] !== transition) {
    t9 = {
      opacity: t7,
      transform: t8,
      transition
    };
    $[15] = t7;
    $[16] = t8;
    $[17] = transition;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== attributes || $[20] !== column || $[21] !== isLocked || $[22] !== listeners || $[23] !== setActivatorNodeRef) {
    t10 = isLocked ? <span aria-label={`${getColumnLabel(column)} is locked`} className="grid size-7 place-items-center" role="img"><LockKeyhole className="size-4 text-muted-foreground" /></span> : <Button aria-label={`Reorder ${getColumnLabel(column)}`} className="cursor-grab touch-none active:cursor-grabbing" id={`table-settings-grip-${column.id}`} ref={setActivatorNodeRef} size="icon-xs" variant="ghost" {...attributes} {...listeners}><GripVertical /></Button>;
    $[19] = attributes;
    $[20] = column;
    $[21] = isLocked;
    $[22] = listeners;
    $[23] = setActivatorNodeRef;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const t11 = canHide ? undefined : `${checkboxId}-reason`;
  let t12;
  if ($[25] !== column) {
    t12 = getColumnLabel(column);
    $[25] = column;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = !canHide;
  let t14;
  if ($[27] !== column || $[28] !== onHide) {
    t14 = value => {
      if (!value) {
        onHide();
      }
      column.toggleVisibility(value);
    };
    $[27] = column;
    $[28] = onHide;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== checkboxId || $[31] !== isVisible || $[32] !== t11 || $[33] !== t12 || $[34] !== t13 || $[35] !== t14) {
    t15 = <Checkbox aria-describedby={t11} aria-label={t12} checked={isVisible} disabled={t13} id={checkboxId} onCheckedChange={t14} data-sentry-element="Checkbox" data-sentry-source-file="settings.tsx" />;
    $[30] = checkboxId;
    $[31] = isVisible;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== column) {
    t16 = getColumnLabel(column);
    $[37] = column;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== checkboxId || $[40] !== t16) {
    t17 = <label className="min-w-0 flex-1 truncate text-sm" htmlFor={checkboxId}>{t16}</label>;
    $[39] = checkboxId;
    $[40] = t16;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== isNew) {
    t18 = isNew && <Badge variant="secondary">New</Badge>;
    $[42] = isNew;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== canHide || $[45] !== checkboxId) {
    t19 = !canHide && <span className="sr-only" id={`${checkboxId}-reason`}>This column cannot be hidden.</span>;
    $[44] = canHide;
    $[45] = checkboxId;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== column || $[48] !== isVisible || $[49] !== onLocate) {
    t20 = isVisible && <Tooltip><TooltipTrigger render={<Button aria-label={`Show ${getColumnLabel(column)} in the table`} className="text-muted-foreground transition-opacity duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:opacity-100 pointer-fine:opacity-0 pointer-fine:group-hover/row:opacity-100" onClick={onLocate} size="icon-xs" variant="ghost" />}><LocateFixed /></TooltipTrigger><TooltipContent>Show in table</TooltipContent></Tooltip>;
    $[47] = column;
    $[48] = isVisible;
    $[49] = onLocate;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== column || $[52] !== extras || $[53] !== isExpanded || $[54] !== showsNewDot) {
    t21 = extras && <CollapsibleTrigger aria-label={`${getColumnLabel(column)}: ${extras.label}${showsNewDot ? ", new" : ""}`} render={<Button className="relative gap-1 px-1.5 text-xs text-muted-foreground" size="xs" variant="ghost" />}>{extras.label}<ChevronDown className={cn("transition-transform duration-150", isExpanded && "rotate-180")} />{showsNewDot && <span className="absolute top-0 right-0 size-1.5 rounded-full bg-primary" />}</CollapsibleTrigger>;
    $[51] = column;
    $[52] = extras;
    $[53] = isExpanded;
    $[54] = showsNewDot;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  let t22;
  if ($[56] !== t10 || $[57] !== t15 || $[58] !== t17 || $[59] !== t18 || $[60] !== t19 || $[61] !== t20 || $[62] !== t21) {
    t22 = <div className="group/row flex animate-in items-center gap-2 rounded-sm px-1.5 py-1.5 duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] fade-in-0 hover:bg-muted/60" ref={rowRef}>{t10}{t15}{t17}{t18}{t19}{t20}{t21}</div>;
    $[56] = t10;
    $[57] = t15;
    $[58] = t17;
    $[59] = t18;
    $[60] = t19;
    $[61] = t20;
    $[62] = t21;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  let t23;
  if ($[64] !== extras || $[65] !== isDragging) {
    t23 = extras && !isDragging && <CollapsibleContent className="mb-1 ml-15 border-l pl-2">{extras.content}</CollapsibleContent>;
    $[64] = extras;
    $[65] = isDragging;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  let t24;
  if ($[67] !== isExpanded || $[68] !== setNodeRef || $[69] !== t22 || $[70] !== t23 || $[71] !== t9) {
    t24 = <Collapsible onOpenChange={setIsExpanded} open={isExpanded} ref={setNodeRef} style={t9} data-sentry-element="Collapsible" data-sentry-component="ColumnRow" data-sentry-source-file="settings.tsx">{t22}{t23}</Collapsible>;
    $[67] = isExpanded;
    $[68] = setNodeRef;
    $[69] = t22;
    $[70] = t23;
    $[71] = t9;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  return t24;
};
const SaveError = t0 => {
  const $ = _c(3);
  const {
    retrySave
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="text-sm text-destructive">Error: Could not save</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== retrySave) {
    t2 = <div className="flex items-center gap-2" role="alert" data-sentry-component="SaveError" data-sentry-source-file="settings.tsx">{t1}<Button onClick={retrySave} size="sm" variant="outline" data-sentry-element="Button" data-sentry-source-file="settings.tsx">Retry</Button></div>;
    $[1] = retrySave;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const getRegionLabel = (region: TableColumnRegion) => regions.find(candidate => candidate.id === region)?.label ?? 'column settings';
function _temp(state) {
  return {
    columnOrder: state.columnOrder,
    columnPinning: state.columnPinning,
    columnVisibility: state.columnVisibility
  };
}
function _temp2(column) {
  return !isSelectionColumn(column.id);
}
function _temp3(column_0) {
  return [column_0.id, column_0];
}
function _temp4(column_1) {
  return column_1.getCanHide();
}
function _temp5(column_2) {
  return !column_2.getIsVisible();
}
function _temp6(column_4) {
  return column_4.id;
}
function _temp7(column_5) {
  return column_5.id;
}
function _temp8(checkbox) {
  return checkbox === document.activeElement;
}
function _temp9(column_6) {
  return column_6.id;
}
function _temp10(column_8) {
  return column_8.id;
}
function _temp11(column_9) {
  return column_9.columnDef.meta?.lockedPinning === "end";
}
function _temp12(column_10) {
  return column_10.id;
}
function _temp13(column_11) {
  return column_11.columnDef.meta?.lockedPinning === "start";
}
function _temp14(column_12) {
  return column_12.id;
}
function _temp15(column_13) {
  return column_13.id;
}
function _temp16(column_14) {
  return column_14.id;
}
function _temp17(column_15) {
  return column_15.columnDef.meta?.lockedPinning === "end";
}
function _temp18(column_16) {
  return column_16.id;
}
function _temp19(column_17) {
  return column_17.columnDef.meta?.lockedPinning === "start";
}
function _temp20(column_18) {
  return column_18.id;
}
function _temp21(column) {
  return !column.columnDef.meta?.lockedPinning;
}
function _temp22(column_0) {
  return column_0.id;
}
function _temp23(t0) {
  const {
    choice: choice_1,
    value
  } = t0;
  return value !== choice_1.default;
}